/**
 * LIFF integration — PRD v1.1 §9, §13.
 *
 * Two things worth knowing before changing anything here:
 *
 * 1. **The ID token is the credential, not the user id.** `liff.getProfile()` returns a
 *    userId, but sending that to the server would let anyone charge as anyone else. We
 *    send `liff.getIDToken()` and the Platform verifies it against LINE (§35.2).
 *
 * 2. **`liff.scanCodeV2()` is not universally available.** It is unsupported in the
 *    LIFF browser on iOS and in external browsers. A QR-first product whose scanner
 *    silently fails on half its devices is broken, so `scan()` reports availability and
 *    the UI always offers manual entry as a first-class path, not a hidden fallback.
 */

import liff from '@line/liff'

const LIFF_ID = import.meta.env.VITE_LIFF_ID as string | undefined
/** Development escape hatch, mirroring the server's InsecureDevVerifier. */
const DEV_USER = import.meta.env.VITE_DEV_USER as string | undefined

export interface LiffState {
  ready: boolean
  inClient: boolean
  scannerAvailable: boolean
  idToken: string | null
  displayName: string | null
  error: string | null
}

export async function initLiff(): Promise<LiffState> {
  // No LIFF id configured: run in dev mode against the server's dev verifier so the
  // whole flow is testable in a plain browser.
  if (!LIFF_ID) {
    return {
      ready: true,
      inClient: false,
      scannerAvailable: false,
      idToken: `dev:${DEV_USER ?? 'U-dev-user'}`,
      displayName: 'Dev User',
      error: null,
    }
  }

  try {
    await liff.init({ liffId: LIFF_ID })

    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri: window.location.href })
      return {
        ready: false,
        inClient: false,
        scannerAvailable: false,
        idToken: null,
        displayName: null,
        error: null,
      }
    }

    const idToken = liff.getIDToken()
    if (!idToken) {
      // liff.isLoggedIn() true but no ID token almost always means the LIFF app is
      // missing the `openid` scope in the LINE console — without it getIDToken()
      // returns null and login() below would silently never be called, leaving every
      // API call to fail with "missing bearer token" and no visible cause.
      return {
        ready: true,
        inClient: liff.isInClient(),
        scannerAvailable: false,
        idToken: null,
        displayName: null,
        error: '登入失敗：無法取得身分權杖，請確認 LIFF 應用程式已啟用 openid 權限。',
      }
    }

    let displayName: string | null = null
    try {
      displayName = (await liff.getProfile()).displayName
    } catch {
      // Profile scope is optional; identity comes from the ID token regardless.
    }

    return {
      ready: true,
      inClient: liff.isInClient(),
      scannerAvailable: liff.isApiAvailable('scanCodeV2'),
      idToken,
      displayName,
      error: null,
    }
  } catch (e) {
    return {
      ready: true,
      inClient: false,
      scannerAvailable: false,
      idToken: null,
      displayName: null,
      error: e instanceof Error ? e.message : 'LIFF 初始化失敗',
    }
  }
}

/** Returns the scanned payload, or null if the user cancelled or scanning failed. */
export async function scan(): Promise<string | null> {
  if (!LIFF_ID || !liff.isApiAvailable('scanCodeV2')) return null
  try {
    const result = await liff.scanCodeV2()
    return result?.value ?? null
  } catch {
    return null
  }
}

export function closeWindow() {
  if (LIFF_ID && liff.isInClient()) liff.closeWindow()
}
